import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
    useTheme,
    useMediaQuery,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Badge,
    Popover
} from '@mui/material';
import {
    Home as HomeIcon,
    Favorite as FavoriteIcon,
    ShoppingBag as ShoppingBagIcon,
    Logout as LogoutIcon,
    KeyboardArrowDown as ArrowDownIcon,
    Add as AddIcon,
    Person as PersonIcon,
    ShoppingCart as ShoppingCartIcon,
    Receipt as ReceiptIcon,
    AccountBalanceWallet as WalletIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import HomePage from '../pages/HomePage';
import LikedPostsPage from '../pages/LikedPostsPage';
import ProductsPage from './ProductPage';
import CreatePostPage from '../pages/CreatePostPage';
import ProfilePage from '../pages/ProfilePage';
import CartPage from '../pages/CartPage';
import Logo from '../assets/Logo.png';
import CustomerOrderList from '../pages/CustomerOrderlist';
import '../styles/ProtectedRoutes.css';
import OrderDetail from '../pages/CustomerOrderdetail';
import ProductDetail from './ProductDetail';
import walletService from '../services/walletservice';

const DRAWER_WIDTH = 240;

const CustomerDashboard = ({ children }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const { cartCount, refreshCart } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
    const [walletBalance, setWalletBalance] = React.useState(null);
    const [walletAnchorEl, setWalletAnchorEl] = React.useState(null);


    useEffect(() => {
        if (!isAuthenticated) {
            setWalletBalance(null);
            return;
        }

        let cancelled = false;
        walletService.getBalance()
            .then((res) => {
                if (!cancelled && res.success) {
                    setWalletBalance(res.data.balance);
                }
            })
            .catch((err) => {
                console.error('Failed to load wallet balance:', err);
            });

        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            toast.error('Logout failed, but you will be redirected to login.');
            navigate('/login');
        }
    };

    const handleLogoutClick = () => {
        setAnchorEl(null);
        setLogoutDialogOpen(true);
    };

    const handleLogoutConfirm = () => {
        setLogoutDialogOpen(false);
        handleLogout();
    };

    const handleLogoutCancel = () => {
        setLogoutDialogOpen(false);
    };

    const handleViewProfile = () => {
        setAnchorEl(null);
        navigate('/profile');
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleWalletClick = (event) => {
        setWalletAnchorEl(event.currentTarget);
    };

    const handleWalletPopoverClose = () => {
        setWalletAnchorEl(null);
    };

    const handleGoToWallet = () => {
        setWalletAnchorEl(null);
        navigate('/wallet');
    };

    const walletPopoverOpen = Boolean(walletAnchorEl);

    // Base nav items always shown, plus auth-only items inserted in their original positions
    const navigationItems = [
        { label: 'Home', icon: <HomeIcon />, path: '/' },
        ...(isAuthenticated
            ? [{ label: 'Liked Posts', icon: <FavoriteIcon />, path: '/liked' }]
            : []),
        ...(isAuthenticated
            ? [{ label: 'Create Post', icon: <AddIcon />, path: '/create-post' }]
            : []),
        { label: 'Our Products', icon: <ShoppingBagIcon />, path: '/products' },
        ...(isAuthenticated
            ? [{ label: 'My Orders', icon: <ReceiptIcon />, path: '/orders' }]
            : []),
    ];

    const getCurrentTabIndex = () => {
        const currentPath = location.pathname;
        const index = navigationItems.findIndex(item => item.path === currentPath);
        return index >= 0 ? index : 0;
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    return (
        <Box className="protected-container" sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Desktop Sidebar */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                        },
                    }}
                    classes={{
                        paper: 'glass-sidebar'
                    }}
                >
                    {/* Sidebar Header */}
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={Logo}
                            alt="Ginni Fashion Studio"
                            className="glass-avatar"
                            sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                            <Typography variant="subtitle1" className="glass-title" sx={{ lineHeight: 1.2 }}>
                                Ginni Fashion
                            </Typography>
                            <Typography variant="caption" className="glass-subtitle" sx={{ lineHeight: 1.2 }}>
                                Studio
                            </Typography>
                        </Box>
                    </Box>

                    <Divider className="glass-divider" />

                    {/* Navigation Items */}
                    <List sx={{ flexGrow: 1, pt: 1, px: 1 }}>
                        {navigationItems.map((item, index) => (
                            <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    className={location.pathname === item.path ? 'nav-item-selected' : 'nav-item-hover'}
                                    sx={{
                                        mx: 1,
                                        borderRadius: 2,
                                        py: 1,
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        slotProps={{
                                            primary: {
                                                fontWeight: location.pathname === item.path ? 600 : 400,
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    <Divider className="glass-divider" />

                    {/* Logout Button - guests don't have a session to log out of */}
                    {isAuthenticated && (
                        <Box sx={{ p: 2 }}>
                            <Button
                                fullWidth
                                className="glass-button-outlined"
                                startIcon={<LogoutIcon />}
                                onClick={handleLogout}
                                size="small"
                            >
                                Logout
                            </Button>
                        </Box>
                    )}
                </Drawer>
            )}

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* Top App Bar for both Mobile and Desktop */}
                <AppBar position="fixed" elevation={1} className="glass-appbar" sx={{ top: 0, zIndex: 1100, left: isMobile ? 0 : DRAWER_WIDTH }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            {isMobile && (
                                <Avatar
                                    src={Logo}
                                    alt="Ginni Fashion Studio"
                                    className="glass-avatar"
                                    sx={{ width: 40, height: 40, mr: 2 }}
                                />
                            )}
                            <Typography
                                variant={isMobile ? "h6" : "h6"}
                                component="div"
                                className="glass-title"
                                sx={{
                                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                                    display: isMobile ? { xs: 'block', sm: 'block' } : 'block'
                                }}
                            >
                                {isMobile ? 'Ginni Fashion Studio' : 'Welcome to Ginni Fashion Studio'}
                            </Typography>
                        </Box>

                        {isAuthenticated && (
                            <Box
                                onClick={handleWalletClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mr: 1.5,
                                    px: isMobile ? 1 : 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                    border: '1px solid rgba(188,150,94,0.3)',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': { backgroundColor: 'rgba(188,150,94,0.1)' },
                                }}
                            >
                                <WalletIcon sx={{ color: '#bc965e', fontSize: isMobile ? 20 : 22 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 600,
                                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {walletBalance !== null
                                        ? `₹${Number(walletBalance).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                        : '—'}
                                </Typography>
                            </Box>
                        )}


                        {/* Wallet Earned Amount Popover */}
                        <Popover
                            open={walletPopoverOpen}
                            anchorEl={walletAnchorEl}
                            onClose={handleWalletPopoverClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            classes={{
                                paper: 'glass-menu'
                            }}
                        >
                            <Box sx={{ p: 2, minWidth: 220, textAlign: 'center', position: 'relative' }}>
                                <IconButton
                                    onClick={handleWalletPopoverClose}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        color: '#bc965e',
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>

                                <WalletIcon sx={{ color: '#bc965e', fontSize: 32, mb: 1, mt: 1 }} />
                                <Typography variant="body2" className="glass-text-primary" sx={{ mb: 0.5 }}>
                                    You have earned
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#bc965e', fontWeight: 700, mb: 1.5 }}
                                >
                                    {walletBalance !== null
                                        ? `₹${Number(walletBalance).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                        : '—'}
                                </Typography>
                            </Box>
                        </Popover>

                        {/* Cart Icon */}
                        <IconButton
                            onClick={() => {
                                console.log('Cart icon clicked, current count:', cartCount);
                                navigate('/cart');
                            }}
                            size="large"
                            className="glass-icon-button"
                            sx={{ mr: 1 }}
                        >
                            <Badge
                                badgeContent={cartCount}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: '#bc965e',
                                        color: '#000',
                                        fontWeight: 600
                                    }
                                }}
                            >
                                <ShoppingCartIcon sx={{ color: '#bc965e', fontSize: isMobile ? 24 : 28 }} />
                            </Badge>
                        </IconButton>

                        {/* User Info Section - Responsive */}
                        {isAuthenticated ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isMobile ? 0.5 : 1
                            }}>
                                <IconButton onClick={handleMenuOpen} size="large" className="glass-icon-button" sx={{ p: 0 }}>
                                    <Avatar className="glass-avatar" sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }}>
                                        {(user?.fullName || user?.name || user?.email)?.charAt(0)?.toUpperCase()}
                                    </Avatar>
                                </IconButton>

                                {/* Show full name only on larger mobile screens and desktop */}
                                <Typography
                                    variant="body2"
                                    className="glass-text-primary"
                                    sx={{
                                        fontWeight: 500,
                                        mr: 0.5,
                                        display: { xs: 'none', sm: isMobile ? 'block' : 'block', md: 'block' },
                                        fontSize: isMobile ? '0.875rem' : '0.875rem',
                                        maxWidth: isMobile ? '120px' : 'none',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {user?.fullName || user?.name || user?.email}
                                </Typography>

                                <IconButton onClick={handleMenuOpen} size="small" className="glass-icon-button">
                                    <ArrowDownIcon sx={{
                                        color: '#bc965e',
                                        fontSize: isMobile ? 18 : 20
                                    }} />
                                </IconButton>
                            </Box>
                        ) : (
                            <Button
                                className="glass-button-outlined"
                                size="small"
                                onClick={() => navigate('/login')}
                                sx={{ ml: 1 }}
                            >
                                Login
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>

                {/* User Menu Dropdown - only rendered for logged-in users */}
                {isAuthenticated && (
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        classes={{ paper: 'glass-menu' }}
                        MenuListProps={{ sx: { pt: 0, pb: 0.5 } }}
                    >
                        <Box sx={{ position: 'relative', minWidth: 180 }}>
                            <IconButton
                                onClick={handleMenuClose}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    color: '#bc965e',
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>

                            <MenuItem onClick={handleViewProfile} className="glass-menu-item" sx={{ pt: '10px' }}>
                                <PersonIcon sx={{ mr: 1, color: '#bc965e' }} />
                                <Typography className="glass-text-primary">View Profile</Typography>
                            </MenuItem>
                            <MenuItem onClick={handleLogoutClick} className="glass-menu-item">
                                <LogoutIcon sx={{ mr: 1, color: '#bc965e' }} />
                                <Typography className="glass-text-primary">Logout</Typography>
                            </MenuItem>
                        </Box>
                    </Menu>
                )}

                {/* Logout Confirmation Dialog */}
                <Dialog
                    open={logoutDialogOpen}
                    onClose={handleLogoutCancel}
                    aria-labelledby="logout-dialog-title"
                    aria-describedby="logout-dialog-description"
                >
                    <DialogTitle id="logout-dialog-title">
                        Confirm Logout
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="logout-dialog-description">
                            Are you sure you want to logout? You will need to sign in again to access your account.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleLogoutCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleLogoutConfirm} color="primary" variant="contained">
                            Logout
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Main Content */}
                <Box
                    component="main"
                    className="protected-content"
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        height: isMobile ? 'calc(100vh - 64px - 56px)' : 'calc(100vh - 64px)', // Account for top bar and bottom nav on mobile
                        mt: '64px', // Add top margin for fixed AppBar
                        mb: isMobile ? '56px' : 0, // Add bottom margin for fixed BottomNavigation on mobile only
                    }}
                >
                    {/* If children are passed explicitly (e.g. guest-accessible ProductDetail), render them.
                        Otherwise, if we're under /home, resolve the nested route ourselves. */}
                    {children ? (
                        <>{children}</>
                    ) : location.pathname.startsWith('/') ? (
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/liked" element={<LikedPostsPage />} />
                            <Route path="/create-post" element={<CreatePostPage />} />
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/products/:productId" element={<ProductDetail />} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/orders" element={<CustomerOrderList />} />
                            <Route path="/orders/:billNumber" element={<OrderDetail />} />
                        </Routes>
                    ) : null}
                </Box>

                {/* Bottom Navigation for Mobile */}
                {isMobile && (
                    <Paper className="glass-bottom-nav" sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
                        <BottomNavigation
                            value={getCurrentTabIndex()}
                            onChange={(_, newValue) => {
                                const item = navigationItems[newValue];
                                if (item) {
                                    handleNavigation(item.path);
                                }
                            }}
                            showLabels
                            sx={{
                                backgroundColor: 'transparent',
                                height: '56px',
                                '& .MuiBottomNavigationAction-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    minWidth: 0,        // allow tabs to shrink below MUI's default 80px min
                                    padding: '6px 2px',
                                    '& .MuiBottomNavigationAction-label': {
                                        fontSize: '0.65rem',
                                        whiteSpace: 'nowrap',
                                    },
                                    '&.Mui-selected': {
                                        color: '#bc965e',
                                        '& .MuiBottomNavigationAction-label': {
                                            fontSize: '0.7rem',
                                        }
                                    }
                                }
                            }}
                        >
                            {navigationItems.map((item, index) => (
                                <BottomNavigationAction
                                    key={index}
                                    label={item.label}
                                    icon={item.icon}
                                />
                            ))}
                        </BottomNavigation>
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default CustomerDashboard;